import { z } from 'zod'
import { latLonSchema } from './dispatch.js'

// ETA Tracker schemas. Unlike the nearest-operative search, this tracks a job's
// *already allocated* officer: their current position, the route they've
// travelled, a Google Routes drive time to site, and a confidence score for
// whether they genuinely look en route. Sourced live from EGIS + Google (see
// @rapid-response/api `eta.ts`) — no persistence. Shared by the REST route
// (/api/v1/eta) and the RPC server function in packages/web.

export const etaQuerySchema = z.object({
  jobNumber: z.string().trim().min(1),
})
export type EtaQuery = z.infer<typeof etaQuerySchema>

/** The officer allocated to the job (the job's executor). */
export const etaOfficerSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  username: z.string().nullable(),
  partnerName: z.string(),
  partnerId: z.string().nullable(),
  phone: z.string().nullable(),
})
export type EtaOfficer = z.infer<typeof etaOfficerSchema>

/** One recorded position on the officer's travelled route. `at` is the ISO
 * coordinate timestamp; `distanceToSiteMiles` is the straight-line distance to
 * the site at that moment (drives the confidence signals + the UI trend). */
export const etaTrackPointSchema = z.object({
  lat: z.number(),
  lon: z.number(),
  at: z.string(),
  distanceToSiteMiles: z.number(),
})
export type EtaTrackPoint = z.infer<typeof etaTrackPointSchema>

// A higher score means the officer looks more genuinely en route. `label`
// summarises the score into a dispatcher-facing verdict.
export const etaConfidenceLabelSchema = z.enum([
  'on_route', // strong, consistent movement toward site
  'uncertain', // mixed signals — worth a check-in
  'off_route', // moving, but not toward the site
  'stationary', // not moving — can't judge direction
  'insufficient_data', // too few/too stale points to score
])
export type EtaConfidenceLabel = z.infer<typeof etaConfidenceLabelSchema>

/** The individual 0–1 signals behind the score, surfaced so the UI (and API
 * callers) can explain *why* — never a black box. */
export const etaConfidenceFactorsSchema = z.object({
  heading: z.number(), // travel bearing vs. bearing to site
  consistency: z.number(), // fraction of steps that reduced distance-to-site
  freshness: z.number(), // recency of the latest coordinate
})
export type EtaConfidenceFactors = z.infer<typeof etaConfidenceFactorsSchema>

export const etaConfidenceSchema = z.object({
  score: z.number().int().min(0).max(100),
  label: etaConfidenceLabelSchema,
  factors: etaConfidenceFactorsSchema,
  reasons: z.array(z.string()),
})
export type EtaConfidence = z.infer<typeof etaConfidenceSchema>

/** Driving distance + time from the officer's current position to site, plus a
 * derived arrival timestamp. Null durations mean Google found no drivable
 * route. `etaIso` is null when there's no duration to project from. */
export const etaDriveSchema = z.object({
  distanceMiles: z.number().nullable(),
  durationSeconds: z.number().nullable(),
  etaIso: z.string().nullable(),
})
export type EtaDrive = z.infer<typeof etaDriveSchema>

/** Why the Kestrel comparison could not be filled in. */
export const KESTREL_UNAVAILABLE_REASONS = ['Kestrel unavailable', 'No Kestrel officer', 'No usable Kestrel readings'] as const

export const kestrelMatchSchema = z.enum(['matched', 'ambiguous', 'not found'])
export const kestrelFreshnessSchema = z.enum(['live', 'allocatable', 'stale', 'silent', 'off'])

export const etaKestrelTrackPointSchema = etaTrackPointSchema.extend({ accuracyM: z.number().nullable() })
export type EtaKestrelTrackPoint = z.infer<typeof etaKestrelTrackPointSchema>

/** The same officer as seen by Kestrel, set beside the EGIS fields. `verified` is false while the
 * Chase operative id is only claimed by the app; `gapMetres` is the distance between the two positions;
 * `discardedReadings` counts the mocked, clock-skewed or invalid readings left out of the track. */
export const etaKestrelSchema = z.object({
  match: kestrelMatchSchema.nullable(),
  verified: z.boolean(),
  currentLocation: latLonSchema.nullable(),
  lastSeen: z.string().nullable(),
  freshness: kestrelFreshnessSchema.nullable(),
  accuracyM: z.number().nullable(),
  gapMetres: z.number().nullable(),
  track: z.array(etaKestrelTrackPointSchema),
  discardedReadings: z.number().int().min(0),
  drive: etaDriveSchema.nullable(),
  confidence: etaConfidenceSchema.nullable(),
  unavailableReason: z.enum(KESTREL_UNAVAILABLE_REASONS).nullable(),
})
export type EtaKestrel = z.infer<typeof etaKestrelSchema>

export const etaResponseSchema = z.object({
  jobNumber: z.string(),
  assetNumber: z.number().int().nullable(),
  assetPostcode: z.string().nullable(),
  site: latLonSchema.nullable(),
  officer: etaOfficerSchema,
  executionStatus: z.string(),
  currentLocation: latLonSchema,
  lastSeen: z.string(),
  drive: etaDriveSchema,
  track: z.array(etaTrackPointSchema),
  confidence: etaConfidenceSchema,
  kestrel: etaKestrelSchema.nullable(),
})
export type EtaResponse = z.infer<typeof etaResponseSchema>

/** Why a board ETA row has no ETA. Argus shows the string as is. */
export const NO_ETA_REASONS = [
  'Job not found',
  'Property has no location',
  'No officer allocated',
  'Officer on site',
  'Officer left site',
  'Job closed',
  'No recent GPS',
  'Routing unavailable',
  'Routing limit reached',
] as const
export type NoEtaReason = (typeof NO_ETA_REASONS)[number]
